import { useContext } from 'react';
import { resolveBillApiUrl } from '../lib/gatewayEndpoint';
import { featureFlagsContext } from '../providers/FeatureFlagsProvider';

export default function useDownloadBill() {
  // Host comes from gatewayEndpoint, not an inline ternary. Two places computing a
  // host that must agree is what left billHistory sending an Apigee token to
  // api-edge. This route sends no Authorization header at all (Apigee's /pdf/bills
  // flow had no OA-VerifyToken), so it is unaffected by the token change.
  //
  // The flag is read from the context for the same reason as in
  // useViewAndDownloadBills: gatewayEndpoint's singleton triggers no re-render, so
  // reading it here pinned the URL to whatever it was at first render. That was
  // observable in prod on 2026-09-05 — /pdf/bills kept going to api-edge for ~40min
  // after useLegacyApigeeEndpoint was flipped to true. Benign on this route because
  // it carries no credential, which is precisely why it went unnoticed.
  const { useLegacyApigeeEndpoint } = useContext(featureFlagsContext);
  const billDownloadUrl = `${resolveBillApiUrl(
    useLegacyApigeeEndpoint,
  )}/pge-bill-api/pdf/bills`;

  /**
   * Downloads the bill with the given encryptedBillId, and returns a blob url to it
   */
  const downloadBill = async (
    encryptedBillId: string | null | undefined,
    isSummary: boolean = false,
    isNonDetailed: boolean = false,
  ) => {
    try {
      const result = await fetch(billDownloadUrl, {
        method: 'POST',
        body: JSON.stringify({
          encryptedBillId,
          isSummary,
          isNonDetailed,
        }),
        headers: {
          'content-type': 'application/json',
        },
      });
      if (!result.ok) {
        throw new Error('Request failed');
      }

      const blob = await result.blob();
      if (blob.size === 0) {
        throw new Error('PDF was empty');
      }

      return URL.createObjectURL(blob);
    } catch (e) {
      return Promise.reject('Bill not found');
    }
  };

  return {
    downloadBill,
  };
}
